import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import {
  createTreeStore,
  getBoundTreeStore,
  ITreeStore,
  ITreeStoreProps,
} from 'components/Tree/CommonTree/useTreeStore';
import { useRef } from 'react';
import { generateGuid } from 'utils/Guid';
import { StoreApi, UseBoundStore } from 'zustand';
import {
  IWorkflowCatalogNode,
  createWorkflowCatalogNode,
  loadWorkflowCatalogNodes,
  loadTree,
  runWorkflowCatalogAction,
} from '../WorkflowCatalogUtils';

export interface IUseWorkflowTreeStoreProps {
  treeId?: string;
  rootNodes?: IWorkflowCatalogNode[];
  onNodeExpanded?: ITreeStoreProps<IWorkflowCatalogNode>['onNodeExpanded'];
  onNodeFolded?: ITreeStoreProps<IWorkflowCatalogNode>['onNodeFolded'];
  waitForCaretAnimationOnExpand?: ITreeStoreProps<IWorkflowCatalogNode>['waitForCaretAnimationOnExpand'];
  onNodeNotFound?: ITreeStoreProps<IWorkflowCatalogNode>['onNodeNotFound'];
  onNodeSelected?: ITreeStoreProps<IWorkflowCatalogNode>['onNodeSelected'];
}

export type IUseWorkflowTreeTypes = ReturnType<typeof useWorkflowTreeStore>;

/**
 * Hook created to reuse functionality between desktop and mobile workflow catalog trees.
 * @param props
 * @returns useful things from the store
 */
export function useWorkflowTreeStore(props: IUseWorkflowTreeStoreProps) {
  // this was component logic, then was moved to a hook, then it needed to be in the store layer,
  // so useRef to just generate it 1x, so no need for useCallback, etc. because it's a factory for all the bits that runs 1x.
  const treeStoreRef = useRef(createWorkflowTreeStore(props));
  const treeStore = treeStoreRef.current;
  const nodes = treeStore(s => s.nodes);
  const isLoading = treeStore(s => s.isLoading);
  const { selectNode, expandNode, foldNode, unselectNode } = treeStore(s => s.actions);
  return {
    expandNode,
    nodes,
    isLoading,
    selectNode,
    foldNode,
    unselectNode,
  };
}

const busyWorkflowTrees = new Map<string, boolean>();

/**
 * Factory, used for the hook in normal components, but also in the `MobilePortalStore`,
 * so it can be driven from outside the component layer if necessary.
 * @param options
 * @returns tree store instance
 */
export function createWorkflowTreeStore({
  treeId,
  rootNodes = [],
  onNodeExpanded,
  onNodeFolded,
  waitForCaretAnimationOnExpand,
  onNodeNotFound,
  onNodeSelected: propsOnNodeSelected,
}: IUseWorkflowTreeStoreProps) {
  if (treeId && getBoundTreeStore<IWorkflowCatalogNode>(treeId)) {
    return getBoundTreeStore<IWorkflowCatalogNode>(treeId)!;
  }

  const id = treeId || generateGuid();
  // track a busy state here - was a useState hook before, but it was not read as hook output anywhere,
  // and need to ref this store in store layer, so...
  const setCanRunWorkflow = (busy: boolean) => (busy ? busyWorkflowTrees.set(id, true) : busyWorkflowTrees.delete(id));
  const canRunWorkflow = () => busyWorkflowTrees.get(id) ?? true;

  const loadNodes = async (parent?: ITreeNode<IWorkflowCatalogNode>): Promise<ITreeNode<IWorkflowCatalogNode>[]> => {
    if (parent?.data) {
      return loadWorkflowCatalogNodes(parent.data);
    } else {
      return loadTree();
    }
  };

  // when this was a hook, it called useCreateTreeStore, which has logic to initialize, and unload tree stores
  // workflows store shouldn't ever need to be cleaned up though
  const treeStore: UseBoundStore<StoreApi<ITreeStore<IWorkflowCatalogNode>>> = createTreeStore<IWorkflowCatalogNode>({
    treeId: id,
    nodes: rootNodes.map(p => createWorkflowCatalogNode(p)),
    doNotUncache: true,
    loadNodes,
    onNodeSelected: propsOnNodeSelected
      ? propsOnNodeSelected
      : async (node, state) => {
          const { expandNode, unselectNode } = state.actions;
          if (node.hasChildren) {
            expandNode(node);
            return;
          }

          if (!canRunWorkflow()) {
            return;
          }

          setCanRunWorkflow(false);

          try {
            if (node.data) {
              await runWorkflowCatalogAction(node.data);
            }
          } finally {
            setCanRunWorkflow(true);
            unselectNode(node);
          }
        },
    onNodeExpanded,
    onNodeFolded,
    waitForCaretAnimationOnExpand,
    onNodeNotFound,
  });

  // initialize it:
  treeStore.getState().actions.initialize();

  return treeStore;
}
